import Link from 'next/link'
import { Button, IconDocumentDefault } from '@mc/design-system'
import { useExperimentVariables } from '@mc/experiments-react'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { GladlySection, GladlyAnswerIndex } from 'libs/utils'
import { useHelpCenterEvents } from '../../events'
import {
  BrandKey,
  genAnswerSlug,
  productsByKey,
  stripHtmlTags,
} from '../../utils'
import { GladlyProductHubHeader } from '../ui'
import classes from './GladlyProductHelpCenter.module.scss'

interface GladlyProductHelpCenterProps {
  brandKey: BrandKey
  sections?: GladlySection[] | null
  pageResults?: GladlyAnswerIndex[] | null
  search: string | null
}

export const GladlyProductHelpCenter = ({
  brandKey,
  sections,
  pageResults,
  search,
}: GladlyProductHelpCenterProps) => {
  const product = productsByKey[brandKey]
  const { t } = useTranslation('@mc/next')

  const { trigger } = useHelpCenterEvents()

  const { brands: enabledByBrand } = useExperimentVariables(
    'dais-2248-decagon-ai-chat-in-help-center',
  )

  const searchResultsSection = {
    id: 'search-results',
    header: t(
      pageResults?.length
        ? 'helpCenter.search.resultsPageTitle'
        : 'helpCenter.search.noResults',
      { search },
    ),
    answers: pageResults ?? [],
  }

  const sectionsToShow = search ? [searchResultsSection] : sections ?? []

  const ctaHref = search
    ? paths.helpCenterForProduct(product.slug)
    : paths.helpCenter

  return (
    <>
      <GladlyProductHubHeader brandKey={brandKey} />
      <div className={classes.mainContainer}>
        <div className='container mc-px-4'>
          <div
            className={`${classes.contentContainer} mc-theme-light mc-h-100 mc-py-4 mc-py-md-9 mc-mx-auto d-flex flex-column flex-gap-4`}
          >
            {sectionsToShow.map((section) => (
              <Section key={section.id} brandKey={brandKey} section={section} />
            ))}
            <Button
              render={
                (enabledByBrand as Record<BrandKey, boolean>)?.[brandKey] ? (
                  <Link href={ctaHref} />
                ) : (
                  <a href={ctaHref} />
                )
              }
              kind='tertiary'
              className='align-self-center mc-mt-10 mc-mb-9'
              onClick={() =>
                trigger('click', {
                  location: 'Product Hub bottom CTA',
                  action: t(
                    search
                      ? 'helpCenter.CTAs.clearSearch'
                      : 'helpCenter.CTAs.backToHelpCenterHub',
                  ),
                })
              }
            >
              {t(
                search
                  ? 'helpCenter.CTAs.clearSearch'
                  : 'helpCenter.CTAs.backToHelpCenterHub',
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

const Section = ({
  brandKey,
  section,
}: {
  brandKey: BrandKey
  section: GladlySection
}) => {
  const product = productsByKey[brandKey]

  const { trigger } = useHelpCenterEvents()

  if (!section.answers.length) return null

  return (
    <section
      aria-labelledby={section.id}
      className={`${classes.section} mc-p-4 mc-corners--3 mc-bg-light`}
    >
      <h2 id={section.id} className='mc-text-h5'>
        {section.header}
      </h2>
      <ul className='mc-mb-2'>
        {section.answers.map((answer) =>
          answer ? (
            <li key={answer.id} className='mc-py-2'>
              <Link
                href={paths.helpCenterAnswer(
                  product.slug,
                  genAnswerSlug(answer.id, answer.name),
                )}
                className='d-flex align-items-center'
                onClick={() =>
                  trigger('click', {
                    location:
                      section.id === 'search-results'
                        ? 'Results Section'
                        : 'Articles Section',
                    action: `Open: ${stripHtmlTags(answer.name)}`,
                  })
                }
              >
                <IconDocumentDefault className='mc-mr-3 mc-opacity--silenced' />
                <span dangerouslySetInnerHTML={{ __html: answer.name }} />
              </Link>
            </li>
          ) : null,
        )}
      </ul>
    </section>
  )
}
